import { isAuregoShop } from '@allesauto/utils/funcs.ts';
import { ArrowRight16, Restart16 } from '@carbon/icons-react';
import React, { MouseEvent, ReactNode } from 'react';

import NextLink from '../nextLink';
import { useWhiteLabel } from '../whiteLabel';

export type TextLinkProps = {
  onClick?: (ev: any) => void;
  isDarkBg?: boolean;
  text: string;
  icon?: ReactNode;
  iconPosition?: 'left' | 'right';
  textColor?: string;
  fillColor?: string;
  customClassName?: string;
  href?: string;
  target?: '_blank' | '_self' | '_top';
  disabled?: boolean;
  noUnderline?: boolean;
  isLoading?: boolean;
  id?: string;
  mobileBlockVariant?: boolean;
  mobileVariantBorderColor?: string;
  isShoppingCart?: boolean;
  isFooter?: boolean;
  linkTextHiddenOnMobileView?: boolean;
};

const TextLink = React.forwardRef<HTMLAnchorElement, TextLinkProps>(
  function TextLink(
    {
      onClick,
      isDarkBg = false,
      text,
      icon,
      iconPosition = 'left',
      fillColor = 'fill-purple80 monochrome:fill-black',
      textColor = 'text-purple80 monochrome:text-black',
      customClassName = '',
      href = '#',
      target = '_self',
      disabled = false,
      noUnderline = false,
      isLoading = false,
      id,
      mobileBlockVariant = false,
      mobileVariantBorderColor = 'outline-purple80 monochrome:outline-black',
      isShoppingCart,
      isFooter,
      linkTextHiddenOnMobileView = false,
      ...restProps
    },
    ref
  ) {
    const withIcon = icon !== null;
    const withBorder = !noUnderline;
    const whiteLabel = useWhiteLabel();

    const onClickHandler = function (ev: MouseEvent) {
      if (href === '#') {
        ev.preventDefault();
      }
      if (onClick) {
        onClick(ev);
      }
    };

    if (isDarkBg) {
      fillColor = 'fill-white monochrome:fill-white';
      textColor = 'text-white monochrome:text-white';
      mobileVariantBorderColor = 'outline-white monochrome:outline-white';
    }
    if (disabled) {
      fillColor = 'fill-grey50 monochrome:fill-grey50';
      textColor = 'text-grey50 monochrome:text-grey50';
      mobileVariantBorderColor = 'outline-grey50 monochrome:outline-grey50';
    }

    const classNames = {
      link: `${textColor} group/textLink inline-flex flex-row no-underline items-center ${
        iconPosition === 'right' && `flex-row-reverse`
      }`,
      linkMobileAsBlock: `flex md:inline-flex flex-col md:flex-row w-full md:w-auto justify-center rounded-[4px] border-1px md:border-0 min-h-[44px] md:min-h-fit ${mobileVariantBorderColor}`,
      icon: `${fillColor} h-5 w-5 ${
        iconPosition === 'right' ? '!ml-5px' : '!mr-5px aurego-link'
      }`,
      iconMobileAsBlock: `ml-0 mr-0 ${
        iconPosition === `right` ? `md:ml-5px` : `md:mr-5px`
      }`,
      loadingIcon: 'animate-spin circle-spinner',
      text: `${isFooter && !isAuregoShop(whiteLabel) ? '!text-white' : ''} ${
        isShoppingCart && '!text-white'
      } relative inline-block leading-5 pb-[2px] overflow-hidden aurego-link ${
        linkTextHiddenOnMobileView ? 'sm:hidden md:inline-block' : ''
      }`,
      textMobileAsBlock: `leading-4 pb-0 md:leading-5 md:pb-[2p]`,
      border: `${fillColor} ${
        isAuregoShop(whiteLabel) && !isShoppingCart && '!fill-black'
      } block absolute bottom-0 h-[2px] w-full transform -translate-x-[24px] group-hover/textLink:translate-x-full transition transition-transform ease-in-out duration-700`,
      primaryBorder: `left-[24px]`,
      secondaryBorder: `-left-full`,
      borderMobileAsBlock: `hidden md:block`,
    };

    const linkTag = (
      <a
        id={id}
        href={href}
        ref={ref}
        target={target}
        onClick={isLoading ? undefined : onClickHandler}
        {...restProps}
        className={`${classNames.link} ${
          mobileBlockVariant && classNames.linkMobileAsBlock
        } ${customClassName}`}
      >
        {withIcon && (
          <div
            className={`${classNames.icon} ${
              mobileBlockVariant && classNames.iconMobileAsBlock
            }`}
            aria-hidden="true"
          >
            {isLoading && <Restart16 className={classNames.loadingIcon} />}
            {!isLoading && (icon ?? <ArrowRight16 />)}
          </div>
        )}
        <div
          className={`${classNames.text} ${
            mobileBlockVariant && classNames.textMobileAsBlock
          }`}
        >
          {text}
          {withBorder && (
            <>
              <svg
                className={`${classNames.border} ${classNames.primaryBorder} ${
                  mobileBlockVariant && classNames.borderMobileAsBlock
                }`}
                aria-hidden="true"
              >
                <rect width="100%" height="100%" />
              </svg>
              <svg
                className={`${classNames.border} ${
                  classNames.secondaryBorder
                } ${mobileBlockVariant && classNames.borderMobileAsBlock}`}
                aria-hidden="true"
              >
                <rect width="100%" height="100%" />
              </svg>
            </>
          )}
        </div>
      </a>
    );

    if (href === '#' && onClick) return linkTag;
    else
      return (
        <NextLink href={isLoading ? {} : href} legacyBehavior>
          {linkTag}
        </NextLink>
      );
  }
);

export default TextLink;
